/**
 * swup（ページ遷移）初期化 + 毎ページモジュールのライフサイクル管理 + ズームスルー剥がれ遷移。
 *
 * - containers は ["main"]。header/footer/splash/Bg は <main> 外なので遷移で差し替わらず永続。
 * - contact/form/complete（miniヘッダー）/ privacy-policy は chrome が別系統なので swup 対象外（フルロード）。
 * - 「毎ページ再初期化が必要なモジュール」は PageModule（mount(scope)=>destroy）で統一管理し、
 *   遷移のたびに旧ページ分を destroy → 新ページの <main> を scope に再 mount する。
 * - header/footer（main外・永続）の演出は初回1回だけ mount する。
 * - スクロールは transition 内で明示制御（PC=Lenis / SP=window）。
 *
 * 遷移アニメ「ズームスルー剥がれ + 一拍ドクン」（参照: addict_intraction / transition-zoom-through-heart）:
 *   parallel-plugin で新旧 <main> を同時にDOMへ保持し、in フェーズで WAAPI 駆動。
 *   旧main=現在位置で固定→上へ剥がれる＋心拍(scale)、新main=奥から手前へズームイン、全画面を控えめ暗転。
 *   物理・イージングは splash.ts の peel と共有（gsap不使用）。尺はバランス（剥がれ800 / 心拍500）。
 */

import Swup from "swup"
import SwupJsPlugin from "@swup/js-plugin"
import SwupParallelPlugin from "@swup/parallel-plugin"
import SwupHeadPlugin from "@swup/head-plugin"
import SwupBodyClassPlugin from "@swup/body-class-plugin"
import SwupPreloadPlugin from "@swup/preload-plugin"
import SwupA11yPlugin from "@swup/a11y-plugin"
import { getLenis } from "./lib-smooth-scroll"
import { headerNav } from "./cmn-header-nav"
import type { PageModule } from "./page-module"
import { mountStory } from "./comp-story"
import { mountHeroReflect } from "./hero-reflect"
import { mountServiceStack } from "./comp-service-stack"
import { mountSolutionOrbit } from "./comp-solution-orbit"
import { mountSectionPeel } from "./cmn-section-peel"
import { mountInview } from "./utils-inview"
import { mountBudoux } from "./lib-budoux-wrap"
import { mountOneviewSlider } from "./comp-oneview-slider"
import { mountInterviewModal } from "./comp-interview-modal"
import { mountFvPulseInScope, mountFvPulseFooter } from "./cmn-fv-pulse"
import { mountIconHoverInScope, mountIconHoverPersistent } from "./cmn-icon-hover"
import { mountLogoContrast } from "./cmn-logo-contrast"

/** 毎ページ再初期化するモジュール群（初回＝全 mount / 遷移＝全 destroy→全 mount） */
const PAGE_MODULES: PageModule[] = [
  mountStory,
  mountHeroReflect,
  mountServiceStack,
  mountSolutionOrbit,
  mountSectionPeel,
  mountInview,
  mountBudoux,
  mountOneviewSlider,
  mountInterviewModal,
  mountFvPulseInScope,
  mountIconHoverInScope,
  mountLogoContrast,
]

/** swup 対象外にするパスセグメント（contact/form/complete/privacy-policy を一括カバー） */
const EXCLUDE_SEGMENTS = ["contact", "form", "privacy-policy"]

/** 遷移アニメ尺(ms)。見本と同じ（剥がれ1500 / 心拍700） */
const PEEL_MS = 1500
const PULSE_MS = 700
/** 剥がれ・ズームのイージング（splash.ts PEEL_EASE と同一） */
const PEEL_EASE = "cubic-bezier(0.75, 0, 0.2, 1)"
/** メニュー(ドロワー/プルダウン)開状態からの遷移で、閉じを見せてから剥がれ開始するまでの待ち(ms)。
 * ドロワーの閉じ＋main縮小の戻り(いずれも 0.6s)を完全に終わらせてから剥がすため 0.6s に合わせる。 */
const MENU_CLOSE_WAIT = 600

/** 現在ページ分の destroy 関数（遷移時に全消化する） */
let cleanups: Array<() => void> = []
/** 遷移開始時の旧ページのスクロール位置（剥がれを現在位置から始めるため保持） */
let pendingScrollY = 0
/** ページごとのスクロール位置。戻る/進む(popstate)で復元する。key=visit URL(path+search) */
const scrollPositions = new Map<string, number>()
/** 遷移後に復元するスクロール位置（popstate=保存値 / 通常遷移=0＝先頭） */
let pendingTargetScroll = 0
/** 旧main へ差し込む .Bg の複製（背景ごと剥がれさせ透けを防ぐ）。visit:start で採取 */
let pendingBgClone: HTMLElement | null = null
/** 遷移中だけ body へ重ねる旧ページのクラス（旧mainの body スコープCSS=.home 等を維持）と、完了後に戻す新クラス */
let pendingOldBodyClass = ""
let pendingNewBodyClass = ""
/** ページ背景色（.home 等は body 背景色を使うため）。旧=旧mainに移す色 / 新=遷移中 body に固定する色 */
let pendingOldBodyBg = ""
let pendingNewBodyBg = ""
/** visit:start で採取した footer のビューポート上端位置(px)。画面内のときだけ数値＝遷移中に旧mainと同じ動きで剥がす（画面外/未採取は null） */
let pendingFooterTop: number | null = null
/** 全画面の暗転ヴェール（遅延生成・永続） */
let veilEl: HTMLElement | null = null

/** reduced-motion 判定 */
const prefersReduced = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * URL が swup 対象外か判定する（ブート抑止と ignoreVisit を兼ねる）。
 * サブディレクトリ設置でも効くよう、パスのいずれかのセグメントが除外語と一致するかで判定。
 * @param {string} url - 判定するURL（相対/絶対どちらでも可）
 * @returns {boolean} 対象外なら true
 */
const isExcluded = (url: string): boolean => {
  let pathname: string
  try {
    pathname = new URL(url, location.href).pathname
  } catch {
    return false
  }
  return pathname
    .split("/")
    .filter(Boolean)
    .some((seg) => EXCLUDE_SEGMENTS.includes(seg))
}

/**
 * 現在アクティブな（新しい）<main> を返す。遷移中は旧main(.is-previous-container)を除外する。
 * @returns {HTMLElement | null}
 */
const activeMain = (): HTMLElement | null =>
  document.querySelector<HTMLElement>("main:not(.is-previous-container)") ||
  document.querySelector<HTMLElement>("main")

/**
 * 現在の <main> を scope に全 PageModule を mount し、destroy を蓄積する。
 */
const mountAll = (): void => {
  const scope = activeMain()
  if (!scope) return
  // スキップリンク(.skipLink href="#main")の飛び先。swupがmainを差し替えるので毎ページ付け直す。
  // tabindex=-1 でフラグメント遷移時にfocusがmainへ移り、次のTabがナビでなく本文へ進む。
  scope.id = "main"
  scope.tabIndex = -1
  PAGE_MODULES.forEach((mount) => {
    const off = mount(scope)
    if (off) cleanups.push(off)
  })
}

/**
 * 蓄積した destroy を全消化する（1つが投げても残りは必ず呼ぶ）。
 */
const destroyAll = (): void => {
  cleanups.forEach((off) => {
    try {
      off()
    } catch {
      /* 1モジュールの失敗で他の解放を止めない */
    }
  })
  cleanups = []
}

/**
 * 遷移完了後、body クラスを新ページの正規クラスのみへ戻す（union で足した旧クラスを除去）。
 */
const restoreBodyClass = (): void => {
  if (pendingNewBodyClass) {
    document.body.className = pendingNewBodyClass
    pendingNewBodyClass = ""
  }
  document.body.style.removeProperty("background-color") // 遷移中に固定した body 背景色を解除（CSSへ戻す）
}

/**
 * 指定Y（既定=先頭0）へ即時スクロール（PC=Lenis / SP=window）。遷移中は両mainがfixedのため不可視。
 * popstate（戻る/進む）では保存位置(pendingTargetScroll)を渡して復元する。
 * @param {number} y - スクロール先Y
 */
const scrollToY = (y: number): void => {
  const lenis = getLenis()
  if (lenis) lenis.scrollTo(y, { immediate: true })
  // SP は html{scroll-behavior:smooth} で scrollTo が滑ってしまう（＝absolute な logo/navCta が引きずられる）ので instant で即時
  else window.scrollTo({ top: y, left: 0, behavior: "instant" as ScrollBehavior })
}

/**
 * 全画面の暗転ヴェールを取得（遅延生成して <body> 直下に置く）。
 * @returns {HTMLElement}
 */
const getVeil = (): HTMLElement => {
  if (veilEl) return veilEl
  veilEl = document.createElement("div")
  veilEl.className = "pageTransition__veil"
  veilEl.setAttribute("aria-hidden", "true")
  document.body.appendChild(veilEl)
  return veilEl
}

/**
 * ズームスルー剥がれ遷移（parallel-plugin で新旧main が同時に居る in フェーズで駆動）。
 * @param {() => void} done - swup へ完了を通知するコールバック
 */
const playZoomThrough = (done: () => void): void => {
  const outgoing = document.querySelector<HTMLElement>("main.is-previous-container")
  const incoming = document.querySelector<HTMLElement>("main:not(.is-previous-container)")
  // 永続footer（containers外なので遷移中も同一要素）。画面内だった時だけ旧mainと一緒に剥がす
  const footerEl = document.querySelector<HTMLElement>(".footer")

  /** footer に付けた凍結inline／アニメを全て外して永続footerを元に戻す */
  const resetFooterPeel = (): void => {
    if (footerEl && pendingFooterTop !== null) {
      footerEl.getAnimations().forEach((a) => a.cancel())
      const s = footerEl.style
      s.position = s.left = s.width = s.top = s.margin = s.zIndex = s.visibility = s.transform = s.opacity = s.filter = ""
    }
    pendingFooterTop = null
  }
  // reduced-motion / 要素欠けは即時完了（parallelがDOMを入れ替えるだけ）
  if (prefersReduced() || !outgoing || !incoming) {
    pendingBgClone = null
    pendingFooterTop = null // footer剥がしはせず（CSSのvisibility:hiddenのまま→is-changing解除で復帰）
    restoreBodyClass()
    done() // スクロール確定・resize は visit:end で（documentがフローに戻った後）
    return
  }

  const y = pendingScrollY
  // 旧main：現在のスクロール位置を凍結（top:-y / height:100vh+y でビューポートに現在の見えを固定＝下部の隙間を防ぐ）
  outgoing.style.top = `${-y}px`
  outgoing.style.height = `calc(100vh + ${y}px)`
  // メニュー開で main に乗った縮小(scale/filter)と header-nav の inline transform-origin をクリア。
  // クリーンな全画面の凍結スナップショットにして剥がれWAAPIと干渉させない（inlineがCSSルールに勝つ）。
  outgoing.style.transition = "none"
  outgoing.style.scale = "1"
  outgoing.style.filter = "none"
  outgoing.style.transformOrigin = "center center"
  // ページ背景の取り合い解消：旧mainは自分のページ背景色を持つ／body は遷移先ページの色に固定（union .home 汚染を打ち消す）
  if (pendingOldBodyBg) outgoing.style.backgroundColor = pendingOldBodyBg
  if (pendingNewBodyBg) document.body.style.backgroundColor = pendingNewBodyBg
  // 背景ごと剥がれるよう .Bg 複製を旧mainの背面に差し込む（透け防止）
  if (pendingBgClone) {
    outgoing.insertBefore(pendingBgClone, outgoing.firstChild)
    pendingBgClone = null
  }
  // popstate（戻る/進む）の復元時は、新mainも保存スクロール位置で凍結（ズーム後にジャンプせず復元位置で着地）
  if (pendingTargetScroll > 0) {
    incoming.style.top = `${-pendingTargetScroll}px`
    incoming.style.height = `calc(100vh + ${pendingTargetScroll}px)`
  }
  // ※実スクロール位置の確定は visit:end（is-changing解除後＝documentがフローに戻り clamp されない）で行う。
  //   遷移中は両mainが fixed で、windowスクロールは document 圧縮により自動的に ~0 へ clamp される（不可視）。

  // 実際の剥がれ開始（メニュー開状態からの遷移は、閉じを見せてから遅延実行する）
  const startPeel = (): void => {
    // 旧main：上へ剥がす（自身の高さぶん translateY）
    outgoing.animate(
      [{ transform: "translateY(0)" }, { transform: "translateY(-100%)" }],
      { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
    )

    // footer が画面内だった場合：永続footerを旧mainと同じピクセル量・同じ尺で一緒に剥がす。
    // footer は containers 外＝差し替わらないので、DOMそのままで見た目だけ動かす（完了時にinlineをリセット）。
    if (footerEl && pendingFooterTop !== null) {
      // peelPx = 旧mainの実高(= 100vh + y)。旧mainの translateY(-100%) とピクセル一致＝ズレずに一体で上がる
      const peelPx = outgoing.getBoundingClientRect().height
      footerEl.style.position = "fixed"
      footerEl.style.left = "0"
      footerEl.style.width = "100%"
      footerEl.style.top = `${pendingFooterTop}px`
      footerEl.style.margin = "0"
      footerEl.style.zIndex = "32" // 旧main(31)より前面＝blank部を覆って footer を見せる
      footerEl.style.visibility = "visible" // is-changing の visibility:hidden を上書き
      footerEl.animate(
        [{ transform: "translateY(0)" }, { transform: `translateY(-${peelPx}px)` }],
        { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
      )
      footerEl.animate(
        [
          { opacity: 1, filter: "blur(0px)" },
          { opacity: 0.85, filter: "blur(6px)" },
        ],
        { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
      )
    }
    // 旧main：わずかにぼけ・薄く
    outgoing.animate(
      [
        { opacity: 1, filter: "blur(0px)" },
        { opacity: 0.85, filter: "blur(6px)" },
      ],
      { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
    )
    // 旧main：一拍ドクン（scale を additive 合成。収縮→オーバーシュート→拡張＝心臓の物理）
    outgoing.animate(
      [
        { transform: "scale(1)", offset: 0, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
        { transform: "scale(0.96)", offset: 0.114, easing: "cubic-bezier(0.37, 0, 0.63, 1)" },
        { transform: "scale(0.98)", offset: 0.229, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
        { transform: "scale(1)", offset: 1 },
      ],
      { duration: PULSE_MS, composite: "add", fill: "both" }
    )

    // 新main：奥（縮小・ぼけ・暗・少し下）から手前（定位置・クリア・明）へズームイン
    const inAnim = incoming.animate(
      [
        { transform: "translateY(8%) scale(0.82)", filter: "blur(12px) brightness(0.5)", opacity: 0.6 },
        { transform: "translateY(0) scale(1)", filter: "blur(0px) brightness(1)", opacity: 1 },
      ],
      { duration: PEEL_MS, easing: PEEL_EASE, fill: "both" }
    )

    // 全画面の控えめ暗転（peak 0.4）
    getVeil().animate(
      [
        { opacity: 0, offset: 0, easing: "cubic-bezier(0.55, 0.085, 0.68, 0.53)" },
        { opacity: 0.4, offset: 0.5, easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" },
        { opacity: 0, offset: 1 },
      ],
      { duration: PEEL_MS, fill: "both" }
    )

    // 最長(剥がれ)完了で done。新mainの fill を解放（以後は永続mainになるため）
    inAnim.finished
      .then(() => {
        incoming.getAnimations().forEach((a) => a.cancel())
        resetFooterPeel()
        restoreBodyClass()
        getLenis()?.resize()
        done()
      })
      .catch(() => {
        resetFooterPeel()
        restoreBodyClass()
        done()
      })
  }

  // メニュー閉じ＋main縮小の戻りは visit:start(is-changing前=生きたページ) で待ち済み。ここは即剥がす。
  startPeel()
}

/**
 * JsPlugin のアニメ定義。parallel-plugin と併用のため out は即時、in で新旧mainを同時アニメ。
 */
const JS_ANIMATIONS = [
  {
    from: "(.*)",
    to: "(.*)",
    /** parallel が out を省くので即時完了 */
    out: (done: () => void): void => done(),
    /** 新旧main が揃った状態でズームスルーを駆動 */
    in: (done: () => void): void => playZoomThrough(done),
  },
]

/**
 * swup を起動し、ライフサイクルフックを配線する。
 */
const initSwup = (): void => {
  // header/footer（main外・永続）の演出は swup の有無に関わらず初回1回だけ
  mountFvPulseFooter()
  mountIconHoverPersistent()

  // miniヘッダーのページ（contact/form/complete）や privacy では swup を起動しない＝全遷移フルロード。
  // それでも毎ページモジュールは通常どおり初回 mount する。
  if (isExcluded(location.pathname)) {
    mountAll()
    return
  }

  history.scrollRestoration = "manual"

  const swup = new Swup({
    containers: ["main"],
    animateHistoryBrowsing: true,
    ignoreVisit: (url: string, { el }: { el?: Element } = {}) => {
      // ページ内アンカー（同一パス＋#hash。例：.Fv__headNav の #approach/#case）は swup で遷移させず、
      // Lenis のスムーススクロール（smooth-scroll.ts のアンカー委譲）に任せる。除外しないと swup が
      // capture でクリックを拾い、同一ページ遷移としてスクロールをtopにリセット→LenisのscrollToを潰す。
      const anchor = el?.closest<HTMLAnchorElement>('a[href*="#"]')
      if (anchor) {
        const u = new URL(anchor.href, location.href)
        if (u.hash && u.pathname === location.pathname) return true
      }
      return (
        !!el?.closest("[data-no-swup]") ||
        // SP幅でアコーディオン化する<a>トグル(.acc__txt.acc-sp-only＝フッター「事業内容」)は
        // 遷移させずアコーディオン開閉のみ効かせる。swupはcaptureでクリックを拾うので、ここで除外しないと
        // アコーディオン側の preventDefault より先にSPA遷移が走ってしまう（アコーディオンの有効化条件と同じ判定）。
        (window.innerWidth <= 1024 && !!el?.closest(".acc__txt.acc-sp-only")) ||
        isExcluded(url)
      )
    },
    plugins: [
      new SwupBodyClassPlugin(), // <body class> を遷移先へ全置換（ページ別CSSスコープの生命線。prefix指定しない）
      new SwupHeadPlugin(), // <title> 等を同期（<html class> は modernizr を壊すため既定のまま触らない）
      new SwupPreloadPlugin(),
      new SwupA11yPlugin(),
      new SwupParallelPlugin({ containers: ["main"] }), // 新旧mainを同時保持＝ズームスルーを成立させる
      new SwupJsPlugin({ animations: JS_ANIMATIONS }),
    ] as any,
  })

  // 離脱時：旧スクロール位置を退避＋メニューを閉じる → その後で旧bodyクラス/背景/.Bg を採取
  swup.hooks.on("visit:start", (visit: any) => {
    pendingScrollY = window.scrollY
    // footer が画面内なら、遷移中に旧mainと同じ動きで剥がすため現在のビューポート上端位置を採取。
    // ※ is-changing 付与後は main が fixed 化して footer が reflow でズレるので、必ずここ（生存中）で採る。
    const footerAtStart = document.querySelector<HTMLElement>(".footer")
    if (footerAtStart) {
      const fRect = footerAtStart.getBoundingClientRect()
      pendingFooterTop = fRect.top < window.innerHeight && fRect.bottom > 0 ? fRect.top : null
    } else {
      pendingFooterTop = null
    }
    // 離脱するページのスクロール位置を保存（戻る/進むで復元するため。from.url をキーに＝popstate時も正しく効く）
    if (visit?.from?.url) scrollPositions.set(visit.from.url, window.scrollY)
    // popstate（ブラウザの戻る/進む）なら遷移先の保存位置へ復元、通常遷移は先頭(0)
    pendingTargetScroll = visit?.history?.popstate ? scrollPositions.get(visit?.to?.url) ?? 0 : 0
    // メニュー(ドロワー/プルダウン)が開いているか（closeAll前に判定）
    const menuOpen = !!document.querySelector(".header__drawer.is-open, .header__pulldown.is-show")
    // 先にメニューを閉じる＝is-menuLock / is-open 等の一時状態クラスを外してから採取する。
    // （これらを union に持ち越すと body.is-menuLock main{scale:.98;filter:blur} が遷移中ずっと再適用され旧mainが縮む）
    headerNav.closeAll()
    pendingOldBodyClass = document.body.className // closeAll後＝一時状態クラスを含まないクリーンな旧クラス
    pendingOldBodyBg = window.getComputedStyle(document.body).backgroundColor // 旧ページの背景色（旧mainへ移す）
    // .Bg を複製して旧main用の背景にする。表示/nami状態を凍結（遷移先ページのbodyクラスに引きずられない）
    const bg = document.querySelector<HTMLElement>(".Bg")
    if (bg) {
      const clone = bg.cloneNode(true) as HTMLElement
      clone.classList.add("Bg--clone")
      clone.style.display = window.getComputedStyle(bg).display // 全体の表示状態を凍結（home=none）
      const cloneNami = clone.querySelector<HTMLElement>(".Bg__nami")
      const origNami = bg.querySelector<HTMLElement>(".Bg__nami")
      if (cloneNami && origNami) cloneNami.style.display = window.getComputedStyle(origNami).display
      pendingBgClone = clone
    } else {
      pendingBgClone = null
    }
    // メニューが開いていたら、is-changing が付く前(=ページが生きてる段階)で「閉じ＋main縮小の戻り(0.98→1)」を
    // 見せてから遷移を進める。visit:start は swup が await するので、ここで待つ＝生きたページ上でなめらかにアニメする。
    if (menuOpen) {
      return new Promise<void>((resolve) => window.setTimeout(resolve, MENU_CLOSE_WAIT))
    }
  })
  // 新DOM描画後（parallelで新旧main同時）：
  // BodyClassPluginが新ページのbodyクラスへ置換済み。旧mainが剥がれる間もその body スコープCSS(.home 等)を
  // 効かせるため、遷移中だけ旧クラスを重ねる（union）。完了時に restoreBodyClass() で新クラスのみへ戻す。
  swup.hooks.on("page:view", () => {
    pendingNewBodyClass = document.body.className
    // union を足す前に新ページの背景色を採取（.home 汚染前の正しい遷移先の色）
    pendingNewBodyBg = window.getComputedStyle(document.body).backgroundColor
    const oldTokens = pendingOldBodyClass.split(/\s+/).filter(Boolean)
    if (oldTokens.length) document.body.classList.add(...oldTokens)
    destroyAll()
    headerNav.refreshMain()
    mountAll()
  })
  // 遷移完了（is-changing解除・documentがフローに戻った後）：新mainの凍結inline(top/height)を解除し、
  // 実スクロール位置を確定する。通常遷移=先頭0／popstate(戻る・進む)=保存位置を復元（この時点なら clamp されない）。
  swup.hooks.on("visit:end", () => {
    const m = activeMain()
    if (m) {
      m.style.removeProperty("top")
      m.style.removeProperty("height")
    }
    getLenis()?.resize()
    scrollToY(pendingTargetScroll)
  })
  // popstate で除外ページ（contact等）へ戻る時はハードナビゲーションにフォールバック
  swup.hooks.on("history:popstate", () => {
    if (isExcluded(location.pathname)) location.href = location.href
  })

  mountAll() // 初回ロード分
}

initSwup()
